import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { chatReducer } from './chatReducer';
import {
  initialChatState,
  ChatState,
  ChatAction,
  ConversationData,
  ChatMessage,
} from './chatTypes';
import { useAuth } from '../../web3/context/AuthContext';
import { createSigner, getChatAddress } from '../signers/signerFactory';
import { Client, ConsentState } from '@xmtp/browser-sdk';
import {
  initializeXmtpClient,
  buildXmtpClient,
  createXmtpClient,
  closeXmtpClient,
  acquireTabLock,
  releaseTabLock,
  canMessage,
  InstallationLimitError,
  registerAfterRevocation,
} from '../services/xmtpClient';
import { backupXmtpDatabase, ensureFirebaseBackup, restoreXmtpDatabase, hasFirebaseBackup } from '../services/xmtpBackup';
import { ensureFirebaseAuth } from '../services/firebaseStorage';
import type { RemoteAttachmentMetadata } from '../services/attachmentService';
import toast from 'react-hot-toast';

const XMTP_ENV = process.env.REACT_APP_NETWORK_TYPE === 'TESTNET' ? 'dev' : 'production';

const ATTACHMENT_PREFIX = '__horus_attachment__';

// Include Unknown + Allowed conversations in all list/stream calls.
// Without this, the XMTP SDK defaults to only Allowed conversations,
// hiding new DMs from unknown senders (ConsentState.Unknown).
const LIST_ALL_CONSENT = { consentStates: [ConsentState.Unknown, ConsentState.Allowed] };

// In-memory cache for inbox ID → Ethereum address resolution.
// Avoids redundant network calls to client.preferences.getLatestInboxState
// for the same inbox IDs during sync (e.g. user's own ID, recurring group members).
const inboxIdAddressCache = new Map<string, string>();

// ---- Context interfaces ----

interface ChatClientContextValue {
  isInitialized: boolean;
  isInitializing: boolean;
  initError: string | null;
  initProgress: string | null;
  hasCloudBackup: boolean | null;
  chatAddress: string | null;
  initializeChat: (password?: string) => Promise<void>;
  showInstallationManager: boolean;
  pendingClient: any;
  pendingInboxId: string | null;
  completeRegistration: () => Promise<void>;
  dismissInstallationManager: () => void;
  revokeInstallations: (installationBytes: Uint8Array[], revokeAll: boolean) => Promise<void>;
}

interface ChatDataContextValue {
  conversations: ConversationData[];
  activeConversationId: string | null;
  messages: Record<string, ChatMessage[]>;
  loadingMessages: Record<string, boolean>;
  hasMoreMessages: Record<string, boolean>;
  unreadCounts: Record<string, number>;
  totalUnread: number;
  searchQuery: string;
  isSyncing: boolean;
  conversationFilter: 'all' | 'dms' | 'groups';
  dispatch: React.Dispatch<ChatAction>;
  lastReadAt: number; // timestamp snapshot from before conversation was marked as read
  sendMessage: (conversationId: string, text: string, attachment?: RemoteAttachmentMetadata) => Promise<void>;
  loadMessages: (conversationId: string) => Promise<void>;
  loadOlderMessages: (conversationId: string) => Promise<void>;
  selectConversation: (conversationId: string | null) => void;
  createDm: (peerAddress: string) => Promise<string | null>;
  createGroup: (memberAddresses: string[], name?: string) => Promise<string | null>;
  addGroupMembers: (conversationId: string, addresses: string[]) => Promise<void>;
  removeGroupMember: (conversationId: string, address: string) => Promise<void>;
  loadGroupMembers: (conversationId: string) => Promise<void>;
  syncConversations: () => Promise<void>;
}

const ChatClientContext = createContext<ChatClientContextValue>({
  isInitialized: false,
  isInitializing: false,
  initError: null,
  initProgress: null,
  hasCloudBackup: null,
  chatAddress: null,
  initializeChat: async () => {},
  showInstallationManager: false,
  pendingClient: null,
  pendingInboxId: null,
  completeRegistration: async () => {},
  dismissInstallationManager: () => {},
  revokeInstallations: async () => {},
});

const ChatDataContext = createContext<ChatDataContextValue>({
  conversations: [],
  activeConversationId: null,
  messages: {},
  loadingMessages: {},
  hasMoreMessages: {},
  unreadCounts: {},
  totalUnread: 0,
  searchQuery: '',
  isSyncing: false,
  conversationFilter: 'all',
  lastReadAt: 0,
  dispatch: () => {},
  sendMessage: async () => {},
  loadMessages: async () => {},
  loadOlderMessages: async () => {},
  selectConversation: () => {},
  createDm: async () => null,
  createGroup: async () => null,
  addGroupMembers: async () => {},
  removeGroupMember: async () => {},
  loadGroupMembers: async () => {},
  syncConversations: async () => {},
});

// ---- Last-read tracking for unread counts ----

const LAST_READ_PREFIX = 'horus-chat-lastread-';

function getLastReadTime(conversationId: string): number {
  const raw = localStorage.getItem(LAST_READ_PREFIX + conversationId);
  return raw ? parseInt(raw, 10) : 0;
}

function setLastReadTime(conversationId: string): void {
  localStorage.setItem(LAST_READ_PREFIX + conversationId, Date.now().toString());
}

// ---- Local cache for instant sidebar + message loading ----

const CONVO_CACHE_PREFIX = 'horus-chat-convos-';
const MSG_CACHE_PREFIX = 'horus-chat-msgs-';
const MSG_CACHE_LIMIT = 30; // max messages cached per conversation

function getCachedConversations(address: string): ConversationData[] | null {
  try {
    const raw = localStorage.getItem(CONVO_CACHE_PREFIX + address.toLowerCase());
    if (!raw) return null;
    const arr = JSON.parse(raw);
    // Revive Date fields
    return arr.map((c: any) => ({
      ...c,
      createdAt: new Date(c.createdAt),
      lastMessageTime: c.lastMessageTime ? new Date(c.lastMessageTime) : undefined,
    }));
  } catch { return null; }
}

function setCachedConversations(address: string, conversations: ConversationData[]): void {
  try {
    localStorage.setItem(CONVO_CACHE_PREFIX + address.toLowerCase(), JSON.stringify(conversations));
  } catch (err) {
    console.warn('[Cache] Failed to save conversations:', err);
  }
}

function getCachedMessages(conversationId: string): ChatMessage[] | null {
  try {
    const key = MSG_CACHE_PREFIX + conversationId;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const arr = JSON.parse(raw);
    return arr.map((m: any) => ({ ...m, sentAt: new Date(m.sentAt) }));
  } catch (err) {
    console.warn('[Cache] Failed to read messages for', conversationId, err);
    return null;
  }
}

function setCachedMessages(conversationId: string, messages: ChatMessage[]): void {
  try {
    // Only cache the most recent messages to avoid localStorage bloat
    const toCache = messages.slice(-MSG_CACHE_LIMIT);
    const key = MSG_CACHE_PREFIX + conversationId;
    localStorage.setItem(key, JSON.stringify(toCache));
  } catch (err) {
    console.warn('[Cache] Failed to save messages for', conversationId, err);
  }
}

/**
 * Merge two message arrays, deduplicating by ID and sorting by time.
 * Preserves messages from both sources (cache + network).
 */
function mergeMessages(a: ChatMessage[], b: ChatMessage[]): ChatMessage[] {
  const map = new Map<string, ChatMessage>();
  // Add a first, then b — b wins on duplicates (newer/resolved data)
  for (const m of a) map.set(m.id, m);
  for (const m of b) map.set(m.id, m);
  return Array.from(map.values()).sort(
    (x, y) => x.sentAt.getTime() - y.sentAt.getTime()
  );
}

/**
 * Update a single conversation's last-message preview in the localStorage cache.
 * Called after sending a message or receiving one via stream, so the sidebar
 * preview survives a page refresh without waiting for syncConversations.
 */
function updateConversationCachePreview(
  address: string,
  conversationId: string,
  text: string,
  time: Date,
  sender: string,
): void {
  const cached = getCachedConversations(address);
  if (!cached) return;
  const idx = cached.findIndex((c) => c.id === conversationId);
  if (idx === -1) return;
  cached[idx].lastMessageText = text;
  cached[idx].lastMessageTime = time;
  cached[idx].lastMessageSender = sender;
  // Re-sort by latest message time
  cached.sort((a, b) => {
    const tA = a.lastMessageTime?.getTime() || a.createdAt.getTime();
    const tB = b.lastMessageTime?.getTime() || b.createdAt.getTime();
    return tB - tA;
  });
  setCachedConversations(address, cached);
}

// ---- Helper to convert XMTP messages to ChatMessage ----

// XMTP internal content types that should not be rendered as user messages
const SYSTEM_CONTENT_TYPES = new Set([
  'group_membership_change',
  'group_updated',
]);

function isSystemContent(content: any): boolean {
  if (typeof content !== 'object' || content === null) return false;
  // XMTP group membership change messages have this shape
  return 'initiatedByInboxId' in content && 'addedInboxes' in content && 'removedInboxes' in content;
}

/**
 * Parse a timestamp from XMTP objects.
 * The browser-sdk uses nanosecond BigInt (sentAtNs, createdAtNs) as primary timestamps.
 * Some SDK versions also expose Date getters (sentAt, createdAt).
 */
function parseXmtpTimestamp(nsValue: any, dateValue: any): Date {
  // Prefer nanosecond BigInt — always present in browser-sdk
  if (nsValue != null) {
    try {
      return new Date(Number(BigInt(nsValue) / BigInt(1_000_000)));
    } catch {
      // Fallback if BigInt conversion fails
    }
  }
  // Date object (getter in some SDK versions)
  if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
    return dateValue;
  }
  // Millisecond number
  if (typeof dateValue === 'number' && dateValue > 0) {
    return new Date(dateValue);
  }
  return new Date();
}

function toMessage(msg: any, myInboxId: string): ChatMessage | null {
  const contentTypeId = msg.contentType?.typeId || '';

  // Skip XMTP internal/system messages
  if (SYSTEM_CONTENT_TYPES.has(contentTypeId) || isSystemContent(msg.content)) {
    return null;
  }

  const senderInboxId = msg.senderInboxId || '';
  let content = msg.content;
  let contentType = contentTypeId || 'text';

  // Detect Horus attachment messages (JSON with prefix)
  if (typeof content === 'string' && content.startsWith(ATTACHMENT_PREFIX)) {
    try {
      content = JSON.parse(content.slice(ATTACHMENT_PREFIX.length));
      contentType = 'remoteAttachment';
    } catch {
      // Not valid JSON, treat as text
    }
  }

  // Skip messages with no displayable content
  if (content === undefined || content === null || content === '') {
    return null;
  }

  const sentAt = parseXmtpTimestamp(msg.sentAtNs, msg.sentAt);

  return {
    id: msg.id || `${sentAt.getTime()}-${senderInboxId}`,
    conversationId: msg.conversationId || '',
    senderAddress: senderInboxId,
    content,
    contentType,
    sentAt,
    // Compare inbox IDs — senderInboxId and client.inboxId are both XMTP inbox IDs
    isSentByMe: senderInboxId.toLowerCase() === myInboxId.toLowerCase(),
  };
}

function getMessagePreview(message: ChatMessage, rawMsg?: any): string {
  if (message.contentType === 'remoteAttachment' && message.content?.isVoiceNote) {
    return '\uD83C\uDFA4 Voice message';
  }
  if (typeof message.content === 'string') {
    return message.content.slice(0, 100);
  }
  return message.contentType === 'remoteAttachment'
    ? '[Attachment]'
    : (typeof rawMsg?.content === 'string' ? rawMsg.content.slice(0, 100) : '[Attachment]');
}

/**
 * Resolve an XMTP inbox ID to an Ethereum address via the client.
 */
async function resolveInboxIdToAddress(client: any, inboxId: string): Promise<string> {
  if (!inboxId) return '';

  const cached = inboxIdAddressCache.get(inboxId);
  if (cached) return cached;

  try {
    const state = await client.preferences.getLatestInboxState(inboxId);
    const ethId = state?.identifiers?.find(
      (id: any) => id.identifierKind === 'Ethereum'
    );
    const address = ethId?.identifier || inboxId;
    inboxIdAddressCache.set(inboxId, address);
    return address;
  } catch {
    return inboxId;
  }
}

async function toConversation(convo: any, client: any): Promise<ConversationData> {
  // Fetch members — in the browser-sdk, members() is an async method
  let rawMembers: any[] = [];
  try {
    rawMembers = typeof convo.members === 'function'
      ? await convo.members()
      : Array.isArray(convo.members) ? convo.members : [];
  } catch {
    // Non-critical
  }

  const isGroup = rawMembers.length > 2 || !!convo.name;

  // Resolve member inbox IDs to Ethereum addresses
  let memberAddresses: string[] = [];
  if (isGroup && rawMembers.length > 0) {
    memberAddresses = await Promise.all(
      rawMembers.map(async (m: any) => {
        const inboxId = m.inboxId || m;
        return resolveInboxIdToAddress(client, inboxId);
      })
    );
  }

  // peerInboxId is an async method in the XMTP SDK, not a property
  let peerInboxId = '';
  if (!isGroup && typeof convo.peerInboxId === 'function') {
    try {
      peerInboxId = await convo.peerInboxId();
    } catch {
      peerInboxId = '';
    }
  }
  // Resolve inbox ID to Ethereum address for display
  const peerAddress = peerInboxId
    ? await resolveInboxIdToAddress(client, peerInboxId)
    : '';

  return {
    id: convo.id || convo.topic,
    topic: convo.topic || convo.id,
    peerAddress,
    isGroup,
    groupName: convo.name || undefined,
    members: memberAddresses,
    createdAt: parseXmtpTimestamp(convo.createdAtNs, convo.createdAt),
    lastMessageText: undefined,
    lastMessageTime: undefined,
  };
}

/**
 * Lightweight version of toConversation for fast initial sidebar render.
 * Resolves peer address for DMs but skips group member resolution (loaded on demand).
 */
async function toConversationLight(convo: any, client: any): Promise<ConversationData> {
  const isGroup = !!convo.name;

  let peerAddress = '';
  if (!isGroup && typeof convo.peerInboxId === 'function') {
    try {
      const peerInboxId = await convo.peerInboxId();
      peerAddress = await resolveInboxIdToAddress(client, peerInboxId);
    } catch { /* non-critical */ }
  }

  return {
    id: convo.id || convo.topic,
    topic: convo.topic || convo.id,
    peerAddress,
    isGroup,
    groupName: convo.name || undefined,
    members: undefined,
    createdAt: parseXmtpTimestamp(convo.createdAtNs, convo.createdAt),
    lastMessageText: undefined,
    lastMessageTime: undefined,
  };
}

// ---- Provider ----

const MESSAGE_PAGE_SIZE = 30;
const ENRICH_BATCH_SIZE = 5;

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(chatReducer, initialChatState);
  const { isLoggedIn, loginOption, entityInfo, email, hasPassword, userWalletsData } = useAuth();
  const streamRef = useRef<any>(null);
  const convoStreamRef = useRef<any>(null);
  const lastReadAtRef = useRef<number>(0);
  const pendingSignerRef = useRef<any>(null);
  const clientRef = useRef(state.client);
  const activeConvoRef = useRef<string | null>(state.activeConversationId);
  const autoInitAttemptedRef = useRef(false);
  const convoMapCacheRef = useRef<Map<string, any>>(new Map());
  const stateConvosRef = useRef(state.conversations);

  // Keep refs in sync with state
  useEffect(() => {
    clientRef.current = state.client;
  }, [state.client]);

  useEffect(() => {
    activeConvoRef.current = state.activeConversationId;
  }, [state.activeConversationId]);

  useEffect(() => {
    stateConvosRef.current = state.conversations;
  }, [state.conversations]);

  const chatAddress = getChatAddress({
    loginOption,
    activePublicKey: entityInfo.activePublicKey,
    email,
    indexWallet: entityInfo.indexWallet,
    hasPassword,
    userWalletsData: userWalletsData as any,
  });

  // ---- Conversation cache helper: avoids redundant conversations.list() calls ----
  const getConversation = useCallback(async (conversationId: string): Promise<any | null> => {
    const client = clientRef.current;
    if (!client) return null;

    const cached = convoMapCacheRef.current.get(conversationId);
    if (cached) return cached;

    // Cache miss — refresh the list
    const allConvos = await client.conversations.list(LIST_ALL_CONSENT);
    convoMapCacheRef.current = new Map(allConvos.map((c: any) => [c.id || c.topic, c]));
    return convoMapCacheRef.current.get(conversationId) || null;
  }, []);

  // ---- Initialize XMTP ----
  const initializeChat = useCallback(
    async (password?: string) => {
      if (state.isInitialized || state.isInitializing) return;

      // Tab lock check
      if (!acquireTabLock()) {
        console.warn('[XMTP Init] acquireTabLock failed', {
          lockValue: localStorage.getItem('horus-xmtp-tab-active'),
        });
        toast.error('Chat is already open in another tab. Please close it first.');
        dispatch({ type: 'SET_INIT_ERROR', payload: 'Chat is active in another tab' });
        return;
      }

      dispatch({ type: 'SET_INITIALIZING', payload: true });
      dispatch({ type: 'SET_INIT_ERROR', payload: null });
      dispatch({ type: 'SET_INIT_PROGRESS', payload: 'Preparing chat...' });

      try {
        const authInfo = {
          loginOption,
          activePublicKey: entityInfo.activePublicKey,
          email,
          indexWallet: entityInfo.indexWallet,
          hasPassword,
          userWalletsData: userWalletsData as any,
        };

        // Close any existing client before creating a new one
        if (clientRef.current) {
          clientRef.current.close();
        }

        dispatch({ type: 'SET_INIT_PROGRESS', payload: 'Setting up wallet signer...' });
        const signer = createSigner(authInfo, password);
        pendingSignerRef.current = signer;
        const address = chatAddress || '';
        const onProgress = (msg: string) => dispatch({ type: 'SET_INIT_PROGRESS', payload: msg });
        const client = await initializeXmtpClient(signer, address, onProgress);

        // Keep signer in ref for periodic background backups
        dispatch({ type: 'SET_INIT_PROGRESS', payload: 'Loading conversations...' });
        dispatch({ type: 'SET_CLIENT', payload: client });
        dispatch({ type: 'SET_INITIALIZED', payload: true });
        dispatch({ type: 'SET_INITIALIZING', payload: false });
      } catch (err: any) {
        if (err?.name === 'InstallationLimitError' && err.inboxId) {
          dispatch({ type: 'SET_INSTALLATION_LIMIT', payload: { client: err.client || null, inboxId: err.inboxId } });
          return;
        }
        pendingSignerRef.current = null;
        console.error('Failed to initialize XMTP:', err);
        releaseTabLock();
        dispatch({
          type: 'SET_INIT_ERROR',
          payload: err.message || 'Failed to initialize chat',
        });
        toast.error('Failed to initialize chat. Please try again.');
      }
    },
    [state.isInitialized, state.isInitializing, loginOption, entityInfo, email, hasPassword, userWalletsData, chatAddress]
  );

  // ---- Complete registration after user revokes installations ----
  const completeRegistration = useCallback(async () => {
    dispatch({ type: 'CLEAR_INSTALLATION_LIMIT' });
    dispatch({ type: 'SET_INITIALIZING', payload: true });
    dispatch({ type: 'SET_INIT_ERROR', payload: null });
    const onProgress = (msg: string) => dispatch({ type: 'SET_INIT_PROGRESS', payload: msg });

    try {
      let client: Client;
      const signer = pendingSignerRef.current;
      const address = chatAddress || '';
      if (state.pendingClient) {
        // We have a client (from register() failure) — just retry registration
        client = await registerAfterRevocation(state.pendingClient, onProgress, signer || undefined, address);
      } else {
        // No client (Client.create() failed) — full re-init with signer
        if (!signer) {
          throw new Error('No signer available. Please try enabling chat again.');
        }
        client = await createXmtpClient(signer, address, onProgress);
      }
      // Keep signer in ref for periodic background backups
      dispatch({ type: 'SET_INIT_PROGRESS', payload: 'Loading conversations...' });
      dispatch({ type: 'SET_CLIENT', payload: client });
      dispatch({ type: 'SET_INITIALIZED', payload: true });
      dispatch({ type: 'SET_INITIALIZING', payload: false });
      toast.success('Device registered successfully.', { duration: 5000 });
    } catch (err: any) {
      console.error('Registration after revocation failed:', err);
      releaseTabLock();
      dispatch({
        type: 'SET_INIT_ERROR',
        payload: err.message || 'Failed to register after revoking installations',
      });
      toast.error('Registration failed. Please try again.');
    }
  }, [state.pendingClient, chatAddress]);

  // ---- Dismiss installation manager without revoking ----
  const dismissInstallationManager = useCallback(() => {
    if (state.pendingClient) {
      try { state.pendingClient.close(); } catch { /* ignore */ }
    }
    pendingSignerRef.current = null;
    releaseTabLock();
    dispatch({ type: 'CLEAR_INSTALLATION_LIMIT' });
  }, [state.pendingClient]);

  // ---- Revoke installations (uses static Client method with stored signer) ----
  const revokeInstallations = useCallback(
    async (installationBytes: Uint8Array[], revokeAll: boolean) => {
      const client = state.pendingClient;
      const inboxId = state.pendingInboxId || client?.inboxId;
      const signer = pendingSignerRef.current;

      // Try instance method first (works if client was created with a signer)
      if (client) {
        try {
          if (revokeAll) {
            await client.revokeAllOtherInstallations();
          } else {
            await client.revokeInstallations(installationBytes);
          }
          return;
        } catch (instanceErr: any) {
          console.warn('Instance revocation failed, trying static method:', instanceErr.message);
        }
      }

      // Fallback: use static Client.revokeInstallations() with the stored signer
      if (!signer || !inboxId) {
        throw new Error('No signer or inbox ID available for revocation');
      }
      await (Client as any).revokeInstallations(
        signer,
        inboxId,
        installationBytes,
        XMTP_ENV,
      );
    },
    [state.pendingClient, state.pendingInboxId]
  );

  // ---- Auto-initialize: try cached DB first, then full init (no user click needed) ----
  useEffect(() => {
    console.log('[XMTP AutoInit] effect', {
      isInitialized: state.isInitialized,
      chatAddress,
      isLoggedIn,
      hasPassword,
      loginOption,
    });
    if (state.isInitialized || !chatAddress || !isLoggedIn) return;
    if (autoInitAttemptedRef.current) return;
    autoInitAttemptedRef.current = true;

    async function tryAutoInit() {
      if (!acquireTabLock()) {
        autoInitAttemptedRef.current = false;
        return;
      }

      dispatch({ type: 'SET_INITIALIZING', payload: true });
      dispatch({ type: 'SET_INIT_ERROR', payload: null });
      const onProgress = (msg: string) => dispatch({ type: 'SET_INIT_PROGRESS', payload: msg });

      try {
        console.log('[XMTP AutoInit] starting', { chatAddress, hasPassword });
        // Step 1: Try to rebuild from cached OPFS database (no signing needed)
        onProgress('Reconnecting from cached session...');
        const cachedClient = await buildXmtpClient(chatAddress!, onProgress);
        if (cachedClient) {
          let registered = false;
          try {
            registered = await cachedClient.isRegistered();
          } catch (regErr) {
            // Network/transient error — local OPFS data is valid, use it anyway.
            // This is common on mobile where connectivity can be flaky.
            console.warn('isRegistered() failed during auto-init (transient), using local client:', regErr);
            registered = true;
          }

          if (registered) {
            // Create signer for background Firebase sync (if possible without user interaction)
            if (!hasPassword) {
              try {
                const authInfo = {
                  loginOption,
                  activePublicKey: entityInfo.activePublicKey,
                  email,
                  indexWallet: entityInfo.indexWallet,
                  hasPassword,
                  userWalletsData: userWalletsData as any,
                };
                const signer = createSigner(authInfo);
                pendingSignerRef.current = signer;
                console.log('[XMTP AutoInit] Signer created, syncing with Firebase for', chatAddress);

                // Two-way sync with Firebase:
                // 1. Upload local state to Firebase (so other devices can restore)
                // 2. Pull Firebase version to local (so next session has latest data)
                (async () => {
                  try {
                    // Upload local to Firebase first
                    await ensureFirebaseBackup(chatAddress!, signer);
                    // Then pull Firebase version to local OPFS for next session.
                    // This ensures local always has Firebase's latest installation data.
                    await restoreXmtpDatabase(chatAddress!, signer);
                    console.log('[XMTP AutoInit] Firebase sync complete — local OPFS updated for next session');
                  } catch (err: any) {
                    console.warn('[XMTP AutoInit] Firebase sync failed:', err?.message || err);
                  }
                })();
              } catch (signerErr: any) {
                console.error('[XMTP AutoInit] Could not create signer for backup:', signerErr?.message || signerErr);
              }
            } else {
              console.log('[XMTP AutoInit] hasPassword=true, skipping backup (signer needs password)');
            }
            onProgress('Loading conversations...');
            dispatch({ type: 'SET_CLIENT', payload: cachedClient });
            dispatch({ type: 'SET_INITIALIZED', payload: true });
            dispatch({ type: 'SET_INITIALIZING', payload: false });
            return;
          }
          cachedClient.close();
        }

        // Step 2: Cache unavailable — try full init with signer (automatic for custodial users)
          // Skip for password-protected users since we can't sign without their password
          if (hasPassword) {
            releaseTabLock();
            dispatch({ type: 'SET_INITIALIZING', payload: false });
            dispatch({ type: 'SET_INIT_PROGRESS', payload: null });

            // Check Firebase for existing backup (custodial users already have Firebase auth)
            (async () => {
              try {
                await ensureFirebaseAuth(chatAddress!);
                const exists = await hasFirebaseBackup(chatAddress!);
                console.log('[XMTP AutoInit] hasPassword=true backup exists?', exists, 'chatAddress=', chatAddress);
                dispatch({ type: 'SET_HAS_CLOUD_BACKUP', payload: exists });
              } catch {
                console.warn('[XMTP AutoInit] hasPassword=true backup check failed');
                dispatch({ type: 'SET_HAS_CLOUD_BACKUP', payload: null });
              }
            })();
            return;
          }

        const authInfo = {
          loginOption,
          activePublicKey: entityInfo.activePublicKey,
          email,
          indexWallet: entityInfo.indexWallet,
          hasPassword,
          userWalletsData: userWalletsData as any,
        };

        onProgress('Setting up wallet signer...');
        const signer = createSigner(authInfo);
        pendingSignerRef.current = signer;
        const client = await initializeXmtpClient(
          signer,
          chatAddress!,
          onProgress,
          { allowCreateNew: false }
        );
        // Keep signer in ref for periodic background backups
        onProgress('Loading conversations...');
        dispatch({ type: 'SET_CLIENT', payload: client });
        dispatch({ type: 'SET_INITIALIZED', payload: true });
        dispatch({ type: 'SET_INITIALIZING', payload: false });
      } catch (err: any) {
        if (err?.name === 'InstallationLimitError' && err.inboxId) {
          dispatch({ type: 'SET_INSTALLATION_LIMIT', payload: { client: err.client || null, inboxId: err.inboxId } });
          return;
        }
        pendingSignerRef.current = null;
        console.warn('Auto-init failed, user must click Enable Chat:', err?.message || err);
        releaseTabLock();
        dispatch({ type: 'SET_INITIALIZING', payload: false });
        dispatch({ type: 'SET_INIT_PROGRESS', payload: null });
      }
    }

    tryAutoInit();
  }, [chatAddress, isLoggedIn, state.isInitialized, loginOption, entityInfo, email, hasPassword, userWalletsData]);

  // ---- Sync conversations (two-phase: fast sidebar, then background enrichment) ----
  const syncConversations = useCallback(async () => {
    const client = clientRef.current;
    if (!client) return;

    dispatch({ type: 'SET_SYNCING', payload: true });
    try {
      // Network sync with retry
      for (let attempt = 1; attempt <= 5; attempt++) {
        try {
          await client.conversations.sync();
          break;
        } catch (err: any) {
          console.warn(`Conversation sync attempt ${attempt}/5 failed:`, err);
          if (attempt < 5) {
            await new Promise((r) => setTimeout(r, 2000));
          }
        }
      }
      // syncAll with timeout to prevent hanging on slow networks.
      // Must include Unknown + Allowed consent states to process welcome
      // messages from new/unknown senders.
      await Promise.race([
        client.conversations.syncAll(LIST_ALL_CONSENT.consentStates),
        new Promise((_, reject) => setTimeout(() => reject(new Error('syncAll timeout')), 10000)),
      ]).catch((err: any) => {
        console.warn('[XMTP Sync] syncAll timed out or failed:', err?.message || err);
      });

      const allConvos = await client.conversations.list(LIST_ALL_CONSENT);
      console.log(`[XMTP Sync] Found ${allConvos.length} total conversations`);

      // Populate conversation object cache
      convoMapCacheRef.current = new Map(allConvos.map((c: any) => [c.id || c.topic, c]));

      // Phase 1: Build lightweight conversation metadata — no per-convo sync or message loading
      const conversations: ConversationData[] = await Promise.all(
        allConvos.map((convo: any) => toConversationLight(convo, client))
      );

      // Merge with cached previews so existing preview data shows immediately
      if (chatAddress) {
        const cachedConvos = getCachedConversations(chatAddress);
        if (cachedConvos) {
          const cachedMap = new Map(cachedConvos.map((c) => [c.id, c]));
          conversations.forEach((convo) => {
            const cached = cachedMap.get(convo.id);
            if (!cached) return;
            if (cached.lastMessageText) {
              convo.lastMessageText = cached.lastMessageText;
              convo.lastMessageTime = cached.lastMessageTime;
              convo.lastMessageSender = cached.lastMessageSender;
            }
          });
        }
      }

      // Sort and dispatch immediately — sidebar renders now
      conversations.sort((a, b) => {
        const timeA = a.lastMessageTime?.getTime() || a.createdAt.getTime();
        const timeB = b.lastMessageTime?.getTime() || b.createdAt.getTime();
        return timeB - timeA;
      });

      dispatch({ type: 'SET_CONVERSATIONS', payload: conversations });
      dispatch({ type: 'SET_SYNCING', payload: false });

      // Phase 2: Enrich with message previews and unread counts in background batches
      const myInboxId = client.inboxId || '';
      for (let i = 0; i < allConvos.length; i += ENRICH_BATCH_SIZE) {
        const batch = allConvos.slice(i, i + ENRICH_BATCH_SIZE);

        await Promise.all(
          batch.map(async (convo: any, batchIdx: number) => {
            const idx = i + batchIdx;
            const convoData = conversations[idx];
            if (!convoData) return;

            try {
              await convo.sync().catch(() => {});
              const recentMsgs = await convo.messages({ limit: BigInt(5) });
              const lastReadTime = getLastReadTime(convoData.id);

              const parsedMsgs = recentMsgs
                .map((m: any) => toMessage(m, myInboxId))
                .filter((m: any): m is ChatMessage => m !== null)
                .sort((a: ChatMessage, b: ChatMessage) => a.sentAt.getTime() - b.sentAt.getTime());

              if (parsedMsgs.length > 0) {
                const latest = parsedMsgs[parsedMsgs.length - 1];
                const textPreview =
                  typeof latest.content === 'string'
                    ? latest.content.slice(0, 100)
                    : latest.content?.isVoiceNote
                      ? '\uD83C\uDFA4 Voice message'
                      : '[Attachment]';

                // Only update if network data is newer than cached preview
                const cachedTime = convoData.lastMessageTime?.getTime() || 0;
                if (latest.sentAt.getTime() >= cachedTime) {
                  dispatch({
                    type: 'UPDATE_CONVERSATION_LAST_MESSAGE',
                    payload: { id: convoData.id, text: textPreview, time: latest.sentAt, sender: latest.senderAddress },
                  });
                }
              }

              // Count unread
              let unread = 0;
              const userSentAfterLastRead = parsedMsgs.some(
                (m: ChatMessage) => m.isSentByMe && m.sentAt.getTime() > lastReadTime
              );
              if (!userSentAfterLastRead) {
                for (const msg of parsedMsgs) {
                  if (!msg.isSentByMe && msg.sentAt.getTime() > lastReadTime) unread++;
                }
              }
              if (unread > 0) {
                dispatch({ type: 'SET_UNREAD_COUNT', payload: { conversationId: convoData.id, count: unread } });
              }
            } catch {
              // Not critical — conversation still shows
            }
          })
        );
      }

      // Persist enriched conversations to localStorage
      if (chatAddress) {
        setCachedConversations(chatAddress, conversations);
      }
    } catch (err) {
      console.error('Error syncing conversations:', err);
      dispatch({ type: 'SET_SYNCING', payload: false });
    }
  }, [chatAddress]);

  // ---- Load messages for a conversation ----
  const loadMessages = useCallback(async (conversationId: string) => {
    const client = clientRef.current;
    if (!client) return;

    // Load cached messages instantly while network sync happens
    const cached = getCachedMessages(conversationId);
    if (cached && cached.length > 0) {
      dispatch({ type: 'SET_MESSAGES', payload: { conversationId, messages: cached } });
    }

    dispatch({ type: 'SET_LOADING_MESSAGES', payload: { conversationId, loading: true } });

    try {
      const convo = await getConversation(conversationId);
      if (!convo) return;

      await convo.sync().catch((err: any) =>
        console.warn('Conversation sync failed, loading cached messages:', err)
      );
      const msgs = await convo.messages({ limit: BigInt(MESSAGE_PAGE_SIZE) });

      const myInboxId = client.inboxId || '';
      const networkMessages: ChatMessage[] = msgs
        .map((m: any) => toMessage(m, myInboxId))
        .filter((m: ChatMessage | null): m is ChatMessage => m !== null)
        .sort((a: ChatMessage, b: ChatMessage) => a.sentAt.getTime() - b.sentAt.getTime());

      // Resolve sender inbox IDs to Ethereum addresses for display
      const uniqueSenderIds = Array.from(new Set(networkMessages.map((m) => m.senderAddress)));
      const addressMap = new Map<string, string>();
      await Promise.all(
        uniqueSenderIds.map(async (inboxId) => {
          const addr = await resolveInboxIdToAddress(client, inboxId);
          addressMap.set(inboxId, addr);
        })
      );
      networkMessages.forEach((m) => {
        m.senderAddress = addressMap.get(m.senderAddress) || m.senderAddress;
      });

      // Merge network messages with cached messages so that any
      // stream-delivered messages in the cache aren't lost if the
      // network sync hasn't caught up yet.
      // Filter out local- placeholder messages from cache (they would
      // duplicate real messages from the network that have different IDs).
      const merged = cached
        ? mergeMessages(
            cached.filter((m) => !m.id.startsWith('local-')),
            networkMessages
          )
        : networkMessages;

      dispatch({ type: 'SET_MESSAGES', payload: { conversationId, messages: merged } });
      dispatch({
        type: 'SET_HAS_MORE_MESSAGES',
        payload: { conversationId, hasMore: msgs.length >= MESSAGE_PAGE_SIZE },
      });

      // Persist merged messages to localStorage
      setCachedMessages(conversationId, merged);

      // Update sidebar preview with the most recent message
      if (merged.length > 0) {
        const latest = merged[merged.length - 1];
        let textPreview: string;
        if (latest.contentType === 'remoteAttachment' && latest.content?.isVoiceNote) {
          textPreview = '\uD83C\uDFA4 Voice message';
        } else if (typeof latest.content === 'string') {
          textPreview = latest.content.slice(0, 100);
        } else {
          textPreview = latest.contentType === 'remoteAttachment' ? '[Attachment]' : '[Attachment]';
        }
        dispatch({
          type: 'UPDATE_CONVERSATION_LAST_MESSAGE',
          payload: {
            id: conversationId,
            text: textPreview,
            time: latest.sentAt,
            sender: latest.senderAddress,
          },
        });
      }
    } catch (err) {
      console.error('Error loading messages:', err);
    } finally {
      dispatch({ type: 'SET_LOADING_MESSAGES', payload: { conversationId, loading: false } });
    }
  }, []);

  // ---- Load older messages (pagination) ----
  const loadOlderMessages = useCallback(
    async (conversationId: string) => {
      const client = clientRef.current;
      if (!client) return;

      const existingMessages = state.messages[conversationId] || [];
      if (existingMessages.length === 0) return;

      dispatch({ type: 'SET_LOADING_MESSAGES', payload: { conversationId, loading: true } });

      try {
        const convo = await getConversation(conversationId);
        if (!convo) return;

        // Use the oldest message's timestamp as cursor to fetch earlier messages
        const oldestMessage = existingMessages[0];
        const beforeNs = BigInt(oldestMessage.sentAt.getTime()) * BigInt(1_000_000);

        const msgs = await convo.messages({
          limit: BigInt(MESSAGE_PAGE_SIZE),
          sentBeforeNs: beforeNs,
        });

        const myInboxId = client.inboxId || '';
        const existingIds = new Set(existingMessages.map((m) => m.id));
        const olderMsgs: ChatMessage[] = msgs
          .map((m: any) => toMessage(m, myInboxId))
          .filter((m: ChatMessage | null): m is ChatMessage => m !== null)
          .filter((m: ChatMessage) => !existingIds.has(m.id))
          .sort((a: ChatMessage, b: ChatMessage) => a.sentAt.getTime() - b.sentAt.getTime());

        // Resolve sender addresses
        const uniqueSenderIds = Array.from(new Set(olderMsgs.map((m: ChatMessage) => m.senderAddress)));
        const addressMap = new Map<string, string>();
        await Promise.all(
          uniqueSenderIds.map(async (inboxId) => {
            const addr = await resolveInboxIdToAddress(client, inboxId);
            addressMap.set(inboxId, addr);
          })
        );
        olderMsgs.forEach((m: ChatMessage) => {
          m.senderAddress = addressMap.get(m.senderAddress) || m.senderAddress;
        });

        if (olderMsgs.length > 0) {
          dispatch({ type: 'PREPEND_MESSAGES', payload: { conversationId, messages: olderMsgs } });
        }
        dispatch({
          type: 'SET_HAS_MORE_MESSAGES',
          payload: { conversationId, hasMore: msgs.length >= MESSAGE_PAGE_SIZE },
        });
      } catch (err) {
        console.error('Error loading older messages:', err);
      } finally {
        dispatch({ type: 'SET_LOADING_MESSAGES', payload: { conversationId, loading: false } });
      }
    },
    [state.messages]
  );

  // ---- Send message ----
  const sendMessage = useCallback(
    async (conversationId: string, text: string, attachment?: RemoteAttachmentMetadata) => {
      const client = clientRef.current;
      if (!client || (!text.trim() && !attachment)) return;

      try {
        const convo = await getConversation(conversationId);
        if (!convo) {
          toast.error('Conversation not found');
          return;
        }

        // Sync conversation state before sending to ensure MLS group is current
        await convo.sync();

        if (attachment) {
          // Send attachment metadata as a prefixed JSON string
          const payload = ATTACHMENT_PREFIX + JSON.stringify(attachment);
          await convo.send(payload);
        } else {
          await convo.send(text.trim());
        }

        // Ensure messages are published to the XMTP network
        await convo.publishMessages();

        // Immediately update sidebar preview so conversation order stays current
        const sentTime = new Date();
        let textPreview: string;
        if (attachment?.isVoiceNote) {
          textPreview = '\uD83C\uDFA4 Voice message';
        } else if (attachment) {
          textPreview = '[Attachment]';
        } else {
          textPreview = text.trim().slice(0, 100);
        }
        const senderAddr = client.inboxId || '';
        dispatch({
          type: 'UPDATE_CONVERSATION_LAST_MESSAGE',
          payload: {
            id: conversationId,
            text: textPreview,
            time: sentTime,
            sender: senderAddr,
          },
        });

        // Persist preview to localStorage so it survives refresh
        if (chatAddress) {
          updateConversationCachePreview(chatAddress, conversationId, textPreview, sentTime, senderAddr);
        }

        // Note: sent message will be cached by the stream handler when it
        // arrives back via streamAllMessages (with the real XMTP message ID).
        // We don't cache a local placeholder here to avoid duplicate messages
        // on refresh (local ID ≠ XMTP ID → merge treats them as different).

        // Mark conversation as read — the user is actively participating
        setLastReadTime(conversationId);
      } catch (err: any) {
        console.error('Error sending message:', err);
        toast.error('Failed to send message');
      }
    },
    [chatAddress]
  );

  // ---- Select conversation ----
  const selectConversation = useCallback((conversationId: string | null) => {
    if (conversationId) {
      // Snapshot the last-read time BEFORE marking as read (for new-messages divider)
      lastReadAtRef.current = getLastReadTime(conversationId);
      dispatch({ type: 'CLEAR_UNREAD', payload: conversationId });
      setLastReadTime(conversationId);
    } else {
      lastReadAtRef.current = 0;
    }
    dispatch({ type: 'SET_ACTIVE_CONVERSATION', payload: conversationId });
  }, []);

  // ---- Create DM ----
  const createDm = useCallback(
    async (peerAddress: string): Promise<string | null> => {
      const client = clientRef.current;
      if (!client) return null;

      try {
        // Check if the address is reachable on XMTP before attempting DM
        const reachable = await canMessage(client, [peerAddress]);
        if (!reachable.get(peerAddress.toLowerCase())) {
          toast.error('This address is not registered on XMTP and cannot receive messages yet.');
          return null;
        }

        const identifier = {
          identifier: peerAddress.toLowerCase(),
          identifierKind: "Ethereum" as const,
        };
        const dm = await client.conversations.newDmWithIdentifier(identifier);

        // Ensure MLS welcome/commit messages are published to the network so
        // the recipient can discover this conversation via syncAll().
        try {
          await dm.publishMessages();
          // Also sync conversations to ensure welcome delivery is propagated
          await client.conversations.sync();
          await client.conversations.syncAll();
          console.log('[XMTP] DM created and welcome published for', peerAddress, '| DM id:', dm.id);
        } catch (publishErr) {
          console.warn('[XMTP] Failed to publish DM welcome message:', publishErr);
        }

        const convoData = { ...(await toConversation(dm, client)), peerAddress: peerAddress.toLowerCase() };
        dispatch({ type: 'ADD_CONVERSATION', payload: convoData });
        return convoData.id;
      } catch (err: any) {
        console.error('Error creating DM:', err);
        toast.error('Failed to start conversation');
        return null;
      }
    },
    []
  );

  // ---- Create Group ----
  const createGroup = useCallback(
    async (memberAddresses: string[], name?: string): Promise<string | null> => {
      const client = clientRef.current;
      if (!client) return null;

      try {
        const identifiers = memberAddresses.map((addr) => ({
          identifier: addr.toLowerCase(),
          identifierKind: "Ethereum" as const,
        }));
        const group = await client.conversations.newGroupWithIdentifiers(
          identifiers,
          { name: name || undefined }
        );

        // Ensure MLS welcome messages are published so all members discover the group
        try {
          await group.publishMessages();
          await client.conversations.sync();
          await client.conversations.syncAll();
          console.log('[XMTP] Group created and welcome messages published | id:', group.id);
        } catch (publishErr) {
          console.warn('[XMTP] Failed to publish group welcome messages:', publishErr);
        }

        const convoData = await toConversation(group, client);
        convoData.isGroup = true;
        convoData.groupName = name;
        dispatch({ type: 'ADD_CONVERSATION', payload: convoData });
        return convoData.id;
      } catch (err: any) {
        console.error('Error creating group:', err);
        toast.error('Failed to create group');
        return null;
      }
    },
    []
  );

  // ---- Add members to group ----
  const addGroupMembers = useCallback(
    async (conversationId: string, addresses: string[]) => {
      const client = clientRef.current;
      if (!client) return;

      // Optimistic update: immediately reflect new members in the UI
      const currentConvo = stateConvosRef.current.find(c => c.id === conversationId);
      const existingMembers = currentConvo?.members || [];
      const newAddrs = addresses
        .map(a => a.toLowerCase())
        .filter(a => !existingMembers.some(m => m.toLowerCase() === a));
      if (newAddrs.length > 0) {
        dispatch({ type: 'UPDATE_CONVERSATION_MEMBERS', payload: { id: conversationId, members: [...existingMembers, ...newAddrs] } });
      }

      try {
        const convo = await getConversation(conversationId);
        if (!convo) throw new Error('Conversation not found');

        await convo.sync();
        const identifiers = addresses.map((addr) => ({
          identifier: addr.toLowerCase(),
          identifierKind: "Ethereum" as const,
        }));
        await (convo as any).addMembersByIdentifiers(identifiers);
        await convo.sync();

        // Reconcile with authoritative member list from SDK
        const updated = await toConversation(convo, client);
        dispatch({ type: 'UPDATE_CONVERSATION_MEMBERS', payload: { id: conversationId, members: updated.members || [] } });
        toast.success(`Added ${addresses.length} member(s)`);
      } catch (err: any) {
        // Revert optimistic update on failure
        dispatch({ type: 'UPDATE_CONVERSATION_MEMBERS', payload: { id: conversationId, members: existingMembers } });
        console.error('Error adding group members:', err);
        toast.error(err.message || 'Failed to add members');
      }
    },
    []
  );

  // ---- Remove member from group ----
  const removeGroupMember = useCallback(
    async (conversationId: string, address: string) => {
      const client = clientRef.current;
      if (!client) return;

      try {
        const convo = await getConversation(conversationId);
        if (!convo) throw new Error('Conversation not found');

        await convo.sync();
        const identifier = {
          identifier: address.toLowerCase(),
          identifierKind: "Ethereum" as const,
        };
        await (convo as any).removeMembersByIdentifiers([identifier]);
        await convo.sync();

        // Refresh member list
        const updated = await toConversation(convo, client);
        dispatch({ type: 'UPDATE_CONVERSATION_MEMBERS', payload: { id: conversationId, members: updated.members || [] } });
        toast.success('Member removed');
      } catch (err: any) {
        console.error('Error removing group member:', err);
        toast.error(err.message || 'Failed to remove member');
      }
    },
    []
  );

  // ---- Load group members on demand ----
  const loadGroupMembers = useCallback(
    async (conversationId: string) => {
      const client = clientRef.current;
      if (!client) return;

      try {
        const convo = await getConversation(conversationId);
        if (!convo) return;

        let rawMembers: any[] = [];
        try {
          rawMembers = typeof convo.members === 'function'
            ? await convo.members()
            : Array.isArray(convo.members) ? convo.members : [];
        } catch { return; }

        const memberAddresses = await Promise.all(
          rawMembers.map(async (m: any) => {
            const inboxId = m.inboxId || m;
            return resolveInboxIdToAddress(client, inboxId);
          })
        );

        dispatch({ type: 'UPDATE_CONVERSATION_MEMBERS', payload: { id: conversationId, members: memberAddresses } });
      } catch (err) {
        console.error('Error loading group members:', err);
      }
    },
    [getConversation]
  );

  // ---- Start message stream when client is ready ----
  useEffect(() => {
    const client = state.client;
    if (!client || !state.isInitialized) return;

    let cancelled = false;

    // Reconnection wrapper: restarts a stream function with exponential backoff
    // when it ends or errors. This handles WebSocket failures gracefully.
    async function runWithReconnect(name: string, fn: () => Promise<void>) {
      let backoffMs = 1000;
      const MAX_BACKOFF = 30000;
      while (!cancelled) {
        try {
          console.log(`[XMTP Stream] Starting ${name}`);
          backoffMs = 1000; // Reset on successful start
          await fn();
          if (cancelled) break;
          console.warn(`[XMTP Stream] ${name} ended, reconnecting in ${backoffMs}ms`);
        } catch (err) {
          if (cancelled) break;
          console.error(`[XMTP Stream] ${name} error, reconnecting in ${backoffMs}ms:`, err);
        }
        await new Promise(r => setTimeout(r, backoffMs));
        backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF);
      }
    }

    async function startStreams() {
      // Stream all messages (throws on failure so runWithReconnect can catch)
      const msgStream = await client!.conversations.streamAllMessages({
        consentStates: [ConsentState.Unknown, ConsentState.Allowed],
      });
      streamRef.current = msgStream;
      console.log('[XMTP Stream] Message stream established');

      for await (const msg of msgStream) {
        if (cancelled) break;
        const myInboxId = client!.inboxId || '';
        const message = toMessage(msg, myInboxId);

        // Skip XMTP system/internal messages (group membership changes, etc.)
        if (!message) continue;
        if (!message.conversationId) {
          console.warn('[XMTP Stream] Dropping message with empty conversationId', msg);
          continue;
        }

        // Resolve sender inbox ID to Ethereum address before dispatching/caching
        try {
          const resolved = await resolveInboxIdToAddress(client!, message.senderAddress);
          if (resolved && resolved !== message.senderAddress) {
            message.senderAddress = resolved;
          }
        } catch { /* non-critical — keep inbox ID */ }

        // If this message is for a conversation not yet in state (e.g. new DM
        // from an unknown user), fetch it from the SDK and add it to the sidebar.
        const knownIds = (stateConvosRef.current || []).map((c: ConversationData) => c.id);
        if (!knownIds.includes(message.conversationId)) {
          const textPreview = getMessagePreview(message, msg);
          // Add a placeholder immediately so incoming chats are never hidden
          // while background sync catches up.
          const placeholderConvo: ConversationData = {
            id: message.conversationId,
            topic: message.conversationId,
            peerAddress: message.senderAddress,
            isGroup: false,
            groupName: undefined,
            members: undefined,
            createdAt: message.sentAt,
            lastMessageText: textPreview,
            lastMessageTime: message.sentAt,
            lastMessageSender: message.senderAddress,
          };
          dispatch({ type: 'ADD_CONVERSATION', payload: placeholderConvo });

          if (chatAddress) {
            const cachedConvos = getCachedConversations(chatAddress) || [];
            if (!cachedConvos.some((c) => c.id === message.conversationId)) {
              setCachedConversations(chatAddress, [placeholderConvo, ...cachedConvos]);
            }
          }

          try {
            await client!.conversations.sync();
            const allConvos = await client!.conversations.list(LIST_ALL_CONSENT);
            convoMapCacheRef.current = new Map(allConvos.map((c: any) => [c.id || c.topic, c]));
            const rawConvo = allConvos.find((c: any) => (c.id || c.topic) === message.conversationId);
            if (rawConvo) {
              const convoData = await toConversationLight(rawConvo, client!);
              dispatch({ type: 'ADD_CONVERSATION', payload: convoData });
            }
          } catch (err) {
            console.warn('Failed to fetch new conversation from stream:', err);
          }
        }

        dispatch({
          type: 'APPEND_MESSAGE',
          payload: { conversationId: message.conversationId, message },
        });

        // Append to message cache (with deduplication)
        const existingCache = getCachedMessages(message.conversationId) || [];
        if (!existingCache.some((m) => m.id === message.id)) {
          existingCache.push(message);
          setCachedMessages(message.conversationId, existingCache);
        }

        // Update conversation's last message
        const textPreview = getMessagePreview(message, msg);
        dispatch({
          type: 'UPDATE_CONVERSATION_LAST_MESSAGE',
          payload: {
            id: message.conversationId,
            text: textPreview,
            time: message.sentAt,
            sender: message.senderAddress,
          },
        });

        // Update conversation cache with new preview
        if (chatAddress) {
          updateConversationCachePreview(chatAddress, message.conversationId, textPreview, message.sentAt, message.senderAddress);
        }

        // Increment unread if not the active conversation and not sent by me
        // Use ref to always get the current activeConversationId (avoids stale closure)
        if (
          message.conversationId !== activeConvoRef.current &&
          !message.isSentByMe
        ) {
          dispatch({ type: 'INCREMENT_UNREAD', payload: message.conversationId });
        }
      }
    }

    async function startConvoStream() {
      const cStream = await client!.conversations.stream();
      convoStreamRef.current = cStream;
      console.log('[XMTP Stream] Conversation stream established');

      for await (const convo of cStream) {
        if (cancelled) break;
        const convoData = await toConversationLight(convo, client!);
        // Update conversation object cache
        convoMapCacheRef.current.set(convoData.id, convo);
        dispatch({ type: 'ADD_CONVERSATION', payload: convoData });
      }
    }

    // Light sync: refresh conversation list and discover new conversations.
    // For newly found conversations, also loads their latest message preview.
    async function lightSync() {
      const syncStart = Date.now();

      // Two-phase sync:
      // 1. conversations.sync() — pulls conversation list/directory from network
      // 2. conversations.syncAll() — processes MLS welcome messages for new conversations
      // Both are needed: sync() discovers conversation metadata, syncAll() processes
      // the actual MLS welcome messages that contain key material for new groups/DMs.
      let synced = false;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          await client!.conversations.sync();
          const syncedCount = await client!.conversations.syncAll(LIST_ALL_CONSENT.consentStates);
          console.log(`[XMTP LightSync] sync + syncAll complete (syncAll returned ${syncedCount})`);
          synced = true;
          break;
        } catch (err) {
          console.warn(`[XMTP LightSync] sync attempt ${attempt}/3 failed:`, err);
          if (attempt < 3) await new Promise(r => setTimeout(r, 1000 * attempt));
        }
      }
      if (!synced) {
        console.warn('[XMTP LightSync] All sync attempts failed, skipping this cycle');
        return;
      }

      try {
        const allConvos = await client!.conversations.list(LIST_ALL_CONSENT);
        convoMapCacheRef.current = new Map(allConvos.map((c: any) => [c.id || c.topic, c]));

        // Check for new conversations not yet in state
        const existingIds = new Set(
          (stateConvosRef.current || []).map((c: ConversationData) => c.id)
        );
        const myInboxId = client!.inboxId || '';
        const newConvoIds: string[] = [];

        for (const convo of allConvos) {
          const id = convo.id || (convo as any).topic;
          if (!existingIds.has(id)) {
            newConvoIds.push(id);
            const convoData = await toConversationLight(convo, client!);

            // Load latest message preview for the new conversation
            try {
              await convo.sync().catch(() => {});
              const recentMsgs = await convo.messages({ limit: BigInt(5) });
              const parsed = recentMsgs
                .map((m: any) => toMessage(m, myInboxId))
                .filter((m: any): m is ChatMessage => m !== null)
                .sort((a: ChatMessage, b: ChatMessage) => a.sentAt.getTime() - b.sentAt.getTime());

              if (parsed.length > 0) {
                const latest = parsed[parsed.length - 1];
                convoData.lastMessageText = typeof latest.content === 'string'
                  ? latest.content.slice(0, 100)
                  : latest.content?.isVoiceNote ? '\uD83C\uDFA4 Voice message' : '[Attachment]';
                convoData.lastMessageTime = latest.sentAt;
                convoData.lastMessageSender = latest.senderAddress;
              }
            } catch { /* non-critical */ }

            dispatch({ type: 'ADD_CONVERSATION', payload: convoData });

            // Persist to cache so it survives refresh
            if (chatAddress) {
              const currentConvos = stateConvosRef.current || [];
              setCachedConversations(chatAddress, [...currentConvos, convoData]);
            }
          }
        }

        const elapsed = Date.now() - syncStart;
        console.log(`[XMTP LightSync] ${newConvoIds.length} new, ${allConvos.length} total conversations (${elapsed}ms)`);
      } catch (err) {
        console.warn('[XMTP LightSync] Failed after sync:', err);
      }
    }

    // Load cached conversations instantly so the sidebar appears immediately
    const cachedConvos = chatAddress ? getCachedConversations(chatAddress) : null;
    if (cachedConvos && cachedConvos.length > 0) {
      dispatch({ type: 'SET_CONVERSATIONS', payload: cachedConvos });
    }

    // Load conversations from local OPFS without waiting for network sync.
    // This ensures fast local retrieval even if sync is slow or offline.
    (async () => {
      try {
        const allConvos = await client!.conversations.list(LIST_ALL_CONSENT);
        if (!allConvos || allConvos.length === 0) return;

        convoMapCacheRef.current = new Map(allConvos.map((c: any) => [c.id || c.topic, c]));

        const conversations: ConversationData[] = await Promise.all(
          allConvos.map((convo: any) => toConversationLight(convo, client!))
        );

        // Merge cached previews so last message text/time renders immediately
        if (cachedConvos && cachedConvos.length > 0) {
          const cachedMap = new Map(cachedConvos.map((c) => [c.id, c]));
          conversations.forEach((convo) => {
            const cached = cachedMap.get(convo.id);
            if (!cached) return;
            if (cached.lastMessageText) {
              convo.lastMessageText = cached.lastMessageText;
              convo.lastMessageTime = cached.lastMessageTime;
              convo.lastMessageSender = cached.lastMessageSender;
            }
          });
        }

        conversations.sort((a, b) => {
          const timeA = a.lastMessageTime?.getTime() || a.createdAt.getTime();
          const timeB = b.lastMessageTime?.getTime() || b.createdAt.getTime();
          return timeB - timeA;
        });

        dispatch({ type: 'SET_CONVERSATIONS', payload: conversations });
      } catch (err) {
        console.warn('Local conversation load failed:', err);
      }
    })();

    // Start streams with auto-reconnection — don't wait for sync to finish.
    // Deduplication in APPEND_MESSAGE handles any overlap.
    runWithReconnect('messages', startStreams);
    runWithReconnect('conversations', startConvoStream);

    // Sync from network in parallel with streams.
    // After initial sync, run periodic light re-syncs to discover new conversations
    // from other users (streams may not reliably detect new incoming DMs in MLS).
    syncConversations().then(() => {
      if (!cancelled) {
        // Quick early re-syncs to catch conversations missed during initial load
        setTimeout(() => { if (!cancelled) lightSync(); }, 5000);
        setTimeout(() => { if (!cancelled) lightSync(); }, 15000);
      }
    });

    // Periodic light sync every 15 seconds to discover new conversations
    // that streams may have missed (XMTP MLS requires sync to process welcome messages)
    const lightSyncInterval = setInterval(() => {
      if (!cancelled) lightSync();
    }, 15000);

    // Periodic background backup: persist OPFS database to Firebase every 5 minutes.
    // Ensures the latest messages are backed up for recovery after key rotation.
    const backupInterval = setInterval(() => {
      const signer = pendingSignerRef.current;
      if (!cancelled && chatAddress && signer) {
        console.log('[XMTP Periodic] Triggering periodic backup for', chatAddress);
        backupXmtpDatabase(chatAddress, signer).catch((err) => {
          console.error('[XMTP Periodic] Periodic backup failed:', err?.message || err);
        });
      } else if (!cancelled) {
        console.log(`[XMTP Periodic] Skipping backup: chatAddress=${chatAddress}, signer=${!!signer}`);
      }
    }, 5 * 60 * 1000);

    return () => {
      cancelled = true;
      clearInterval(backupInterval);
      clearInterval(lightSyncInterval);
      streamRef.current?.return?.();
      convoStreamRef.current?.return?.();
    };
  }, [state.client, state.isInitialized]);

  // ---- Cleanup on logout ----
  useEffect(() => {
    if (!isLoggedIn && (state.isInitialized || state.pendingClient)) {
      closeXmtpClient(state.client);
      if (state.pendingClient) {
        try { state.pendingClient.close(); } catch { /* ignore */ }
      }
      pendingSignerRef.current = null;
      streamRef.current?.return?.();
      convoStreamRef.current?.return?.();
      autoInitAttemptedRef.current = false;
      inboxIdAddressCache.clear();
      convoMapCacheRef.current = new Map();
      dispatch({ type: 'RESET' });
    }
  }, [isLoggedIn]);

  // ---- Context values ----
  const clientContextValue: ChatClientContextValue = {
    isInitialized: state.isInitialized,
    isInitializing: state.isInitializing,
    initError: state.initError,
    initProgress: state.initProgress,
    hasCloudBackup: state.hasCloudBackup,
    chatAddress,
    initializeChat,
    showInstallationManager: state.showInstallationManager,
    pendingClient: state.pendingClient,
    pendingInboxId: state.pendingInboxId,
    completeRegistration,
    dismissInstallationManager,
    revokeInstallations,
  };

  const dataContextValue: ChatDataContextValue = {
    conversations: state.conversations,
    activeConversationId: state.activeConversationId,
    messages: state.messages,
    loadingMessages: state.loadingMessages,
    hasMoreMessages: state.hasMoreMessages,
    unreadCounts: state.unreadCounts,
    totalUnread: state.totalUnread,
    searchQuery: state.searchQuery,
    isSyncing: state.isSyncing,
    conversationFilter: state.conversationFilter,
    lastReadAt: lastReadAtRef.current,
    dispatch,
    sendMessage,
    loadMessages,
    loadOlderMessages,
    selectConversation,
    createDm,
    createGroup,
    addGroupMembers,
    removeGroupMember,
    loadGroupMembers,
    syncConversations,
  };

  return (
    <ChatClientContext.Provider value={clientContextValue}>
      <ChatDataContext.Provider value={dataContextValue}>
        {children}
      </ChatDataContext.Provider>
    </ChatClientContext.Provider>
  );
}

export const useChatClient = () => useContext(ChatClientContext);
export const useChatData = () => useContext(ChatDataContext);
